import React, { useState, useEffect } from 'react';
import { Bug, Send, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { supabase } from '../lib/supabaseClient';
import { useSession } from '../providers/AuthProvider';
import { useToastStore } from './ui/toaster';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * BugReportModal - Modal for submitting bug reports
 * Collects user description and automatically captures metadata
 */
export function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const { user } = useSession();
  const { toast } = useToastStore();
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Fetch user role from profiles table
  useEffect(() => {
    if (!user?.id) {
      setUserRole(null);
      return;
    }

    const fetchUserRole = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profile?.role) {
          setUserRole(profile.role);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [user?.id]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDescription('');
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      toast({
        title: 'Fejl',
        description: 'Du skal være logget ind for at rapportere en fejl',
        variant: 'error',
      });
      return;
    }

    if (!description.trim()) {
      toast({
        title: 'Fejl',
        description: 'Beskriv venligst fejlen',
        variant: 'error',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create metadata object with automatic information
      const metadata = {
        url: window.location.href,
        userAgent: navigator.userAgent,
        userRole: userRole || 'UNKNOWN',
        timestamp: new Date().toISOString(),
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        screen: {
          width: window.screen.width,
          height: window.screen.height,
        },
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
      };

      // Insert bug report into Supabase
      const { data: bugReport, error: insertError } = await supabase
        .from('bug_reports')
        .insert({
          user_id: user.id,
          user_description: description.trim(),
          status: 'NEW',
          metadata: metadata,
        })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Find admin users to notify
      const { data: adminProfiles, error: adminError } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .eq('role', 'ADMIN')
        .eq('status', 'approved')
        .limit(10); // Notify up to 10 admins

      // Send notifications to admins about the new bug report
      if (adminProfiles && adminProfiles.length > 0) {
        const notificationPromises = adminProfiles.map(async (admin) => {
          try {
            const { error: notificationError } = await supabase.rpc('create_notification', {
              p_recipient_user_id: admin.id,
              p_sender_role: 'SYSTEM',
              p_title: 'Ny fejlrapport modtaget',
              p_message_body: `En ny fejlrapport er blevet indsendt af ${userRole || 'en bruger'}. Se detaljerne for at håndtere den.`,
              p_link_url: `/admin/bug-reports/${bugReport.id}`,
            });

            if (notificationError) {
              console.error(`Failed to notify admin ${admin.id}:`, notificationError);
            }
          } catch (error) {
            console.error(`Error creating notification for admin ${admin.id}:`, error);
          }
        });

        // Don't fail the whole operation if notifications fail
        await Promise.allSettled(notificationPromises);
      } else if (adminError) {
        console.error('Error fetching admin profiles for notification:', adminError);
      }

      // Show success message
      toast({
        title: 'Tak for din feedback',
        description: 'Din fejlrapport er blevet sendt til vores team',
        variant: 'success',
      });

      // Close modal and reset form
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Error submitting bug report:', error);
      toast({
        title: 'Fejl',
        description: 'Kunne ikke indsende fejlrapport. Prøv venligst igen.',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-red-500" />
            <DialogTitle>Rapportér Fejl</DialogTitle>
          </div>
          <DialogDescription>
            Beskriv fejlen eller problemet, så vi kan hjælpe dig bedre.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="bug-description" className="block text-sm font-medium text-app-text mb-2">
              Beskrivelse *
            </label>
            <Textarea
              id="bug-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beskriv hvad der gik galt, hvad du forventede skulle ske, og hvordan vi kan reproducere fejlen..."
              rows={6}
              required
              disabled={isSubmitting}
              className="resize-y"
            />
            <p className="mt-2 text-xs text-app-text-tertiary">
              Automatisk information (URL, browser, rolle) bliver inkluderet i rapporten.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annuller
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !description.trim()}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sender...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Indsend Rapport
                </>
              )}
            </Button>
          </DialogFooter>
        </form>

        <DialogClose onClick={onClose} />
      </DialogContent>
    </Dialog>
  );
}

